import React, { useContext } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button } from '@rneui/themed';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { H6, Paragraph } from '../text';
import { Avatar } from '../common/Avatar';
import CdnImage from '../common/CdnImage';
import ComponentContainer from '../common/ComponentContainer';
import ComponentTitle from '../common/ComponentTitle';
import { HorizontalRule } from '../common/HorizontalRule';
import { StyledWebView } from '../common/StyledWebView';
import { H5 } from '../text';
import LayoutContext, { MODERN_LAYOUT_COLLAPSE_WIDTH } from '../../contexts/Layout';

const STYLED_WEB_VIEW_ID = 'newsArticle';

const NewsArticle = ({
    articleCategory,
    author,
    avatar,
    featured,
    image,
    link,
    linkText,
    releaseDate,
    text,
    title,
    width,
    nodeTypeAlias = 'NewsArticle',
    followContentLink,
    hardWidth
}) => {
    const { layout } = useContext(LayoutContext);
    const parsedReleaseDate = typeof releaseDate === 'string' ? parseISO(releaseDate) : new Date(releaseDate);
    const date = format(parsedReleaseDate, 'MMMM d, yyyy');
    const mobileDate = format(parsedReleaseDate, 'MMM do yyyy');

    const mapTags = () =>
        articleCategory.map((category, index, array) => {
            const txt = ` ${category} ${index + 1 !== array.length ? ',' : ''}`;
            const extraStyle = {
                color: '#162b4a',
                fontFamily: 'GothamBook'
                //marginRight: index + 1 !== array.length ? 2 : undefined
            };

            return <Paragraph key={category} extraStyle={extraStyle} text={txt} wrapperStyle={{ width: undefined }} />;
        });

    return (
        <View
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <View style={{ alignItems: 'center', justifyContent: 'center', marginBottom: layout.styleGuide.spacer6 }}>
                {!!title && <ComponentTitle title={title} style={{ marginBottom: 0 }} />}
                <ComponentContainer style={width <= MODERN_LAYOUT_COLLAPSE_WIDTH ? Styles.byLineContainer : Styles.byLineContainer.wide} width={width}>
                    {avatar?.cdnImagePath && <Avatar imageUri={avatar.cdnImagePath} size={layout.device.isMobile ? 80 : 100} />}
                    <View>
                        <H5
                            style={{ color: '#000', fontFamily: 'GothamBook' }}
                            text={layout.device.isMobile ? `Published by ${author} on ${mobileDate}` : `Published by ${author} on ${date}`}
                        />
                        <View style={{ flexDirection: 'row' }}>
                            <H6 text={articleCategory.join(', ')} />
                        </View>
                    </View>
                </ComponentContainer>
            </View>

            <CdnImage uri={image.cdnImagePath} width={hardWidth} />

            <View style={{ marginHorizontal: layout.styleGuide.spacer1, marginTop: layout.styleGuide.spacer6 }}>
                <StyledWebView html={text} nodeId={STYLED_WEB_VIEW_ID} width={width} />
            </View>

            {!!linkText && !!link && (
                <Button
                    containerStyle={StyleSheet.flatten(Styles.linkButtonContainer, { marginHorizontal: layout.styleGuide.spacer2 })}
                    buttonStyle={Styles.linkButton}
                    icon={{
                        color: '#153d6e',
                        name: 'chevron-circle-right',
                        size: layout.device.isMobile ? 16 : 32,
                        type: 'font-awesome-5'
                    }}
                    iconContainerStyle={{ paddingRight: 4 }}
                    title={linkText}
                    titleStyle={StyleSheet.flatten(layout.styleGuide.h5, { color: '#153d6e', fontFamily: 'GothamBook', margin: 0 })}
                    type='clear'
                    size={layout.device.isMobile ? 'sm' : 'md'}
                    onPress={async () => await followContentLink(link.isExternal, link.internalKey, link.externalUrl)}
                />
            )}

            <View style={{ marginTop: layout.styleGuide.spacer2 }} dataSet={{ spacerTop: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}>
                <HorizontalRule color='#dde4e9' marginHorizontal={layout.styleGuide.spacer2} />
            </View>

            <View
                style={StyleSheet.flatten(Styles.tagsContainer, {
                    marginHorizontal: layout.styleGuide.spacer1,
                    marginVertical: layout.styleGuide.spacer3
                })}
                dataSet={{
                    spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`,
                    spacerLeft: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})`,
                    spacerRight: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})`,
                    spacerTop: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`
                }}
            >
                <Text
                    style={StyleSheet.flatten(layout.styleGuide.p1, { color: '#7f868b', fontFamily: 'GothamBook', marginRight: layout.styleGuide.spacer2 })}
                    dataSet={{ spacerRight: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
                >
                    Tags:
                </Text>
                <View style={{ flexDirection: 'row' }}>{mapTags()}</View>
            </View>

            <View style={{ marginBottom: layout.styleGuide.spacer2 }} dataSet={{ spacerBottom: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}>
                <HorizontalRule color='#dde4e9' marginHorizontal={layout.styleGuide.spacer2} />
            </View>
        </View>
    );
};

const Styles = StyleSheet.create({
    byLineContainer: {
        alignItems: 'center',
        marginBottom: 5,

        wide: {
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            marginBottom: 5
        }
    },
    linkButton: {
        backgroundColor: '#fff',
        borderColor: '#153d6e',
        borderWidth: 2,
        borderRadius: 10
    },
    linkButtonContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        marginBottom: 5
    },
    tagsContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    }
});

NewsArticle.propTypes = {
    articleCategory: PropTypes.array,
    author: PropTypes.string,
    avatar: PropTypes.shape({
        cdnImagePath: PropTypes.string
    }),
    featured: PropTypes.bool,
    link: PropTypes.object,
    linkText: PropTypes.string,
    ministryGroup: PropTypes.array,
    releaseDate: PropTypes.string,
    rootPath: PropTypes.string,
    text: PropTypes.string,
    title: PropTypes.string
};

export default NewsArticle;
